import { KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { EmojiModal } from "../../../../components/EmojiModal";
import { Label } from "../../../../components/Form/Label/styles";
import { ImageField, ImageFieldAction, ImageFieldFrame, ImageFieldRow } from "../../../../components/ImageField";
import { CropProps } from "../../../../components/UploadImageInput";
import { EmojiIcon } from "../../../../icons/Emoji";

export type ThumbnailType = "IMAGE" | "EMOJI" | "NONE";

const cropOptions: CropProps = {
  slice: { aspect: 1 },
  size: { width: 400, height: 400 },
  cropShape: "rect",
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* the emoji on white, as on the page */
  .emoji {
    display: grid;
    place-content: center;
    width: 56px;
    height: 56px;
    font-size: 26px;
    background: var(--color-neutral-white);
  }
`;

/* The kinds, as a segmented control on top of the frame */
const Kinds = styled.div`
  display: flex;
  gap: 3px;
  padding: 3px;
  margin: 12px 12px 0;
  background: var(--color-neutral-darkness);
  border-radius: 10px;

  button {
    flex: 1;
    height: 30px;
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-neutral-150);
    cursor: pointer;
    background: none;
    border-radius: 8px;
    transition:
      background-color 0.15s,
      color 0.15s;

    &:hover {
      color: var(--color-neutral-white);
    }

    &[aria-checked="true"] {
      color: var(--color-neutral-white);
      background: var(--color-neutral-base);
    }

    &:focus-visible {
      outline: 2px solid var(--color-neutral-white);
    }
  }

  /* alone in the frame ("Nenhuma"): the frame's padding below it too */
  &:only-child {
    margin-bottom: 12px;
  }
`;

type Props = {
  /** The kinds the item takes: Links have "Nenhuma" too. */
  types: ThumbnailType[];
  thumbnailType: ThumbnailType;
  onThumbnailChange: (type: ThumbnailType) => void;
  image?: string;
  emoji?: string;
  onChangeImage: (image?: string) => void;
  onChangeEmoji: (emoji?: string) => void;
};

/** The thumbnail of a link, a highlight or a price: an image or an emoji (or none), all in one frame. */
export const ItemThumbnail = ({
  types,
  thumbnailType,
  onThumbnailChange,
  image,
  emoji,
  onChangeImage,
  onChangeEmoji,
}: Props) => {
  const { t } = useTranslation();
  const [isPickingEmoji, setPickingEmoji] = useState(false);
  const prefix = "pages.editor.links.item.form.thumbnail";
  const labelId = useId();
  const kindsId = useId();

  // The kinds sit on top of a different block for each kind, so they're drawn anew when it changes:
  // a kind picked with the arrows gets the focus back
  const refocus = useRef(false);
  useEffect(() => {
    if (refocus.current) {
      refocus.current = false;
      document.getElementById(kindsId)?.querySelector<HTMLButtonElement>("[aria-checked=true]")?.focus();
    }
  }, [thumbnailType, kindsId]);

  // A radio group: the arrows move to the next kind and pick it (around the ends), Tab leaves the group
  const onKindsKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) {
      return;
    }
    event.preventDefault();
    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>("[role=radio]"));
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = (Math.max(current, 0) + step + types.length) % types.length;
    buttons[next].focus();
    refocus.current = true;
    onThumbnailChange(types[next]);
  };

  const kinds = (
    <Kinds id={kindsId} role="radiogroup" aria-labelledby={labelId} onKeyDown={onKindsKeyDown}>
      {types.map(type => (
        <button
          type="button"
          role="radio"
          key={type}
          aria-checked={thumbnailType === type}
          tabIndex={thumbnailType === type ? 0 : -1}
          onClick={() => onThumbnailChange(type)}>
          {t(`${prefix}.${type.toLowerCase()}`)}
        </button>
      ))}
    </Kinds>
  );

  return (
    <Container>
      {isPickingEmoji && (
        <EmojiModal
          onSelect={emojiData => {
            onChangeEmoji(emojiData.emoji);
            setPickingEmoji(false);
          }}
          onCancel={() => setPickingEmoji(false)}
        />
      )}
      <Label as="p" id={labelId}>
        {t(`${prefix}.label`)}
      </Label>

      {thumbnailType === "IMAGE" && (
        <ImageField
          header={kinds}
          shape="square"
          crop={cropOptions}
          title={t(`${prefix}.image`)}
          hint={t("components.imageField.hints.square")}
          image={image}
          onChange={onChangeImage}
        />
      )}

      {thumbnailType === "EMOJI" && (
        <ImageFieldRow
          header={kinds}
          shape="square"
          thumb={emoji ? <span className="emoji">{emoji}</span> : undefined}
          emptyIcon={<EmojiIcon />}
          wrapThumb={thumb => (
            <span role="presentation" onClick={() => setPickingEmoji(true)}>
              {thumb}
            </span>
          )}
          title={t(`${prefix}.emoji`)}
          actions={
            <>
              <ImageFieldAction
                type="button"
                $tone={emoji ? "default" : "primary"}
                onClick={() => setPickingEmoji(true)}>
                {t(`${prefix}.${emoji ? "changeEmoji" : "addEmoji"}`)}
              </ImageFieldAction>
              {emoji && (
                <ImageFieldAction type="button" $tone="danger" onClick={() => onChangeEmoji(undefined)}>
                  {t("components.imageField.remove")}
                </ImageFieldAction>
              )}
            </>
          }
        />
      )}

      {thumbnailType === "NONE" && <ImageFieldFrame>{kinds}</ImageFieldFrame>}
    </Container>
  );
};
